import { useCallback, useEffect, useState } from 'react'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import type { TatTarget } from './types'
import { createTatTarget, deleteTatTarget, fetchTatTargets, updateTatTarget } from './useAdminApi'
import { SettingsFormModal } from './SettingsFormModal'
import { AdminTable, Banner, Btn, Field, SectionHeader, Td, Th, inputCls } from './ui'

const EMPTY: TatTarget = {
  route_id: '*', hub_code: '', kind: 'dwell', from_hub_code: '',
  target_minutes: 60, day_type: 'all', valid_from: new Date().toISOString().slice(0, 10),
}

export function TatSection({ configured }: { configured: boolean }) {
  const [rows, setRows] = useState<TatTarget[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [banner, setBanner] = useState<{ tone: 'ok' | 'error'; msg: string } | null>(null)
  const [editing, setEditing] = useState<TatTarget | null>(null)
  const [isNew, setIsNew] = useState(false)

  const load = useCallback(async () => {
    if (!configured) { setLoading(false); return }
    setLoading(true)
    setError(null)
    try {
      setRows(await fetchTatTargets())
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load TAT targets')
    } finally {
      setLoading(false)
    }
  }, [configured])

  useEffect(() => { void load() }, [load])

  const openNew = () => { setEditing({ ...EMPTY }); setIsNew(true) }
  const openEdit = (t: TatTarget) => { setEditing({ ...t }); setIsNew(false) }

  const save = async () => {
    if (!editing?.hub_code?.trim()) {
      setBanner({ tone: 'error', msg: 'Hub code is required' })
      return
    }
    try {
      if (isNew) await createTatTarget(editing)
      else await updateTatTarget(editing)
      setBanner({ tone: 'ok', msg: isNew ? 'Target created' : 'Target updated' })
      setEditing(null)
      await load()
    } catch (e) {
      setBanner({ tone: 'error', msg: e instanceof Error ? e.message : 'Save failed' })
    }
  }

  const remove = async (t: TatTarget) => {
    if (!window.confirm(`Delete TAT target ${t.route_id}/${t.kind}/${t.hub_code}?`)) return
    try {
      await deleteTatTarget(t)
      setBanner({ tone: 'ok', msg: 'Target deleted' })
      await load()
    } catch (e) {
      setBanner({ tone: 'error', msg: e instanceof Error ? e.message : 'Delete failed' })
    }
  }

  if (!configured) {
    return (
      <p className="text-sm text-slate-500">
        The reporting database is not configured. TAT targets need it to load.
      </p>
    )
  }

  return (
    <div className="space-y-4">
      <SectionHeader
        title="TAT targets"
        subtitle="Turnaround time SLAs by route, hub, and leg type (dwell or transit)."
        action={<Btn onClick={openNew}><span className="inline-flex items-center gap-1"><Plus size={14} /> Add target</span></Btn>}
      />
      {banner ? <Banner tone={banner.tone} onDismiss={() => setBanner(null)}>{banner.msg}</Banner> : null}
      {error ? <Banner tone="error">{error}</Banner> : null}
      {loading ? <p className="text-sm text-slate-500">Loading…</p> : (
        <AdminTable>
          <thead>
            <tr>
              <Th>Route</Th>
              <Th>Kind</Th>
              <Th>Hub</Th>
              <Th>From hub</Th>
              <Th>Minutes</Th>
              <Th>Day</Th>
              <Th />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((t) => (
              <tr key={`${t.route_id}|${t.kind}|${t.from_hub_code}|${t.hub_code}|${t.day_type}|${t.valid_from}`} className="hover:bg-slate-50/80">
                <Td mono>{t.route_id}</Td>
                <Td>{t.kind}</Td>
                <Td mono>{t.hub_code}</Td>
                <Td mono>{t.from_hub_code || '—'}</Td>
                <Td mono>{t.target_minutes ?? '—'}</Td>
                <Td>{t.day_type}</Td>
                <Td>
                  <div className="flex gap-1">
                    <button type="button" onClick={() => openEdit(t)} className="rounded p-1 text-slate-500 hover:bg-slate-100" title="Edit">
                      <Pencil size={14} />
                    </button>
                    <button type="button" onClick={() => void remove(t)} className="rounded p-1 text-rose-500 hover:bg-rose-50" title="Delete">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </Td>
              </tr>
            ))}
            {!rows.length ? (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-sm text-slate-400">No TAT targets</td></tr>
            ) : null}
          </tbody>
        </AdminTable>
      )}

      <SettingsFormModal
        open={editing !== null}
        title={isNew ? 'New TAT target' : 'Edit TAT target'}
        onClose={() => setEditing(null)}
        onSave={() => void save()}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Route ID">
            <input className={inputCls} value={editing?.route_id ?? ''} disabled={!isNew}
              onChange={(e) => setEditing({ ...editing!, route_id: e.target.value })} />
          </Field>
          <Field label="Kind">
            <select className={inputCls} value={editing?.kind ?? 'dwell'} disabled={!isNew}
              onChange={(e) => setEditing({ ...editing!, kind: e.target.value })}>
              <option value="dwell">dwell</option>
              <option value="transit">transit</option>
            </select>
          </Field>
          <Field label="Hub code">
            <input className={inputCls} value={editing?.hub_code ?? ''} disabled={!isNew}
              onChange={(e) => setEditing({ ...editing!, hub_code: e.target.value })} />
          </Field>
          <Field label="From hub (transit only)">
            <input className={inputCls} value={editing?.from_hub_code ?? ''} disabled={!isNew}
              onChange={(e) => setEditing({ ...editing!, from_hub_code: e.target.value })} />
          </Field>
          <Field label="Target minutes">
            <input className={inputCls} type="number" value={editing?.target_minutes ?? ''}
              onChange={(e) => setEditing({ ...editing!, target_minutes: Number(e.target.value) })} />
          </Field>
          <Field label="Day type">
            <select className={inputCls} value={editing?.day_type ?? 'all'} disabled={!isNew}
              onChange={(e) => setEditing({ ...editing!, day_type: e.target.value })}>
              <option value="all">all</option>
              <option value="weekday">weekday</option>
              <option value="weekend">weekend</option>
            </select>
          </Field>
          <Field label="Valid from">
            <input className={inputCls} type="date" value={editing?.valid_from?.slice(0, 10) ?? ''} disabled={!isNew}
              onChange={(e) => setEditing({ ...editing!, valid_from: e.target.value })} />
          </Field>
        </div>
      </SettingsFormModal>
    </div>
  )
}
